import { z } from 'zod';

import { SubmitPriorAuthorizationBlockConfigSchema } from './config.js';

const OrderSpecificQuestionSchema = z.object({
  question: z.string(),
  code: z.string(),
  answer: z.string().nullable(),
  comment: z.string().optional().nullable(),
});

const DiagnosisSchema = z.object({
  description: z.string(),
  icd10Codes: z.array(z.string()).nullable(),
});

const PopulatedProcedureSchema = z.object({
  id: z.string().uuid(),
  patientOrderId: z.string().uuid(),
  standardProcedureId: z.string().nullable(),
  description: z.string(),
  cptCode: z.string().nullable(),
  areaOfInterest: z.string().nullable(),
  reasonForExam: z.string().nullable(),
  ehrReferenceValue: z.string().nullable(),
  rawDiagnoses: z.string().nullable(),
  diagnoses: z.array(DiagnosisSchema).nullable(),
});

const AuthorizationSubmissionSchema = z.object({
  id: z.string().uuid(),
  updated: z.string(),
  created: z.string(),
  patientOrderId: z.string().uuid(),
  payorPortal: z.string().nullable(),
  requestedDate: z.string().nullable(),
  obtainedDate: z.string().nullable(),
  effectiveDate: z.string().nullable(),
  expirationDate: z.string().nullable(),
  trackingNumber: z.string().nullable(),
  authorizationNumber: z.string().nullable(),
  notRequiredReason: z.string().nullable(),
  notRequiredComment: z.string().nullable(),
  exceptionReason: z.string().nullable(),
  exceptionComment: z.string().nullable(),
  rejectedReason: z.string().nullable(),
  rejectedComment: z.string().nullable(),
  pendingReason: z.string().nullable(),
  pendingComment: z.string().nullable(),
  verificationGsPath: z.string().nullable(),
  status: z.string(),
  rawStatus: z.string(),
  procedures: z.array(PopulatedProcedureSchema).nullable(),
});

const DocumentMetadataResponse = z.object({
  id: z.string(),
  date: z.string(),
  type: z.string(),
  title: z.string(),
  summary: z.string().optional(),
  source: z.string(),
  // Allowing string rawJson to be backwards compatible with previously extracted documents
  rawJson: z.union([z.string(), z.record(z.string(), z.unknown())]).nullable(),
});

export const DocumentTranscriptionResponsesSchema = z
  .array(
    z.object({
      documentId: z.string(),
      text: z.string().optional(),
      formattedText: z.string().optional(),
      error: z.string().optional(),
      metadata: DocumentMetadataResponse.optional(),
    })
  )
  .nullable();

export type DocumentTranscriptionResponses = z.infer<typeof DocumentTranscriptionResponsesSchema>;

export const AdditionalContextSchema = z
  .object({
    payorPlanNameSelectValue: z.string().nullish(),
    physicianFaxNumber: z.string().nullish(),
    requestingPhysicianName: z.string().nullish(),
  })
  .nullable();

export const FhirCodingSchema = z.object({
  system: z.string().nullish(),
  code: z.string().nullish(),
});

export const FhirObservationComponentSchema = z.object({
  value: z.string().nullish(),
  codings: z.array(FhirCodingSchema).nullish(),
});

export const FhirObservationReferenceRangeSchema = z.object({
  low: z.number().nullish(),
  high: z.number().nullish(),
  text: z.string().nullish(),
});

export const FhirLabValueSchema = z
  .object({
    id: z.string(),
    loinc: z.string().nullish(),
    date: z.string().nullish(),
    notes: z.string().nullish(),
    value: z.string(),
    testName: z.string().nullish(),
    components: z.array(FhirObservationComponentSchema).nullish(),
    encounter_id: z.string().nullish(),
    encounterTypes: z.array(z.string()).nullish(),
    referenceRanges: z.array(FhirObservationReferenceRangeSchema).nullish(),
  })
  .nullable();

export const FhirLabValuesSchema = z.array(FhirLabValueSchema);
export type FhirLabValues = z.infer<typeof FhirLabValuesSchema>;

export const CopilotDataContextObjectSchema = z
  .object({
    documents: DocumentTranscriptionResponsesSchema.optional(),
    labValues: FhirLabValuesSchema.optional(),
    relevantDocumentIds: z.array(z.string()).nullish(),
    additionalContext: AdditionalContextSchema.optional().nullable(),
  })
  .nullable();
export type CopilotDataContextObject = z.infer<typeof CopilotDataContextObjectSchema>;

export const BasePatientOrderSchema = z.object({
  id: z.string().uuid(),
  created: z.string(),
  updated: z.string(),
  patientId: z.string(),
  practiceId: z.string(),
  patientEhrId: z.string(),
  alternateReferredToEhrProviderId: z.string().nullable(),
  deletedTimestamp: z.string().nullable(),
  dischargeTime: z.string().nullable(),
  ehrCreated: z.string().nullable(),
  ehrInformationId: z.string(),
  ehrReferenceValue: z.string().nullable(),
  ehrScheduled: z.string().nullable(),
  ehrTimeSlotBookingId: z.string().nullable(),
  encounterId: z.string().nullable(),
  externalDocumentPacketId: z.string().nullable(),
  fileName: z.string().nullable(),
  fin: z.string().nullable(),
  followUpUnit: z.string().nullable(),
  followUpValue: z.number().nullable(),
  followUpVisitType: z.string().nullable(),
  isTest: z.boolean().nullable(),
  patientAddressId: z.string().nullable(),
  patientHospitalLocation: z.string().nullable(),
  patientType: z.string().nullable(),
  rawDiagnoses: z.string().nullable(),
  rawProcedures: z.string().nullable(),
  reasonForVisit: z.string().nullable(),
  referralComment: z.string().nullable(),
  referralCreated: z.string().nullable(),
  referralPriority: z.string().nullable(),
  referredByDepartment: z.string().nullable(),
  referredByEhrProviderId: z.string(),
  referredToDepartment: z.string().nullable(),
  referredToEhrProviderId: z.string().nullable(),
  registrationTime: z.string().nullable(),
  requestedAppointmentTimePeriodStart: z.string().optional().nullable(),
  requestedAppointmentTimePeriodEnd: z.string().optional().nullable(),
  requestListStatus: z.string().nullable(),
  scheduleLimitDate: z.string().nullable(),
  seriesAppointmentCount: z.number().nullable(),
  specialty: z.string().nullable(),
  status: z.string().optional().nullable(),
  payer: z.string().nullable(),
  authRequestedDate: z.string().nullable(),
  authObtainedDate: z.string().nullable(),
  authEffectiveDate: z.string().nullable(),
  authExpirationDate: z.string().nullable(),
  reasonForExam: z.string().nullable(),
  trackingNumber: z.string().nullable(),
  payerAuthorizationNumber: z.string().nullable(),
  ehrOrderingEncounterReferenceValue: z.string().nullable(),
});

export const PatientOrderPopulatedSchema = BasePatientOrderSchema.extend({
  authorizationStatus: z.string().nullable(),
  authorizationNote: z.string().nullable(),
  authorizationExceptionReason: z.string().nullable(),
  authorizationExceptionComment: z.string().nullable(),
  authorizationNotRequiredReason: z.string().nullable(),
  authorizationNotRequiredComment: z.string().nullable(),
  authorizationRejectedReason: z.string().nullable(),
  authorizationRejectedComment: z.string().nullable(),
  verificationGsPath: z.string().nullable(),
  diagnoses: z.array(DiagnosisSchema).nullable(),
  procedures: z.array(PopulatedProcedureSchema).nullable(),
  authorizationSubmissions: z.array(AuthorizationSubmissionSchema),
  orderSpecificQuestions: z.array(OrderSpecificQuestionSchema).nullable(),
});

export const SubmitPriorAuthorizationParsedConfigSchema =
  SubmitPriorAuthorizationBlockConfigSchema.extend({
    priorAuthorizationInformation:
      SubmitPriorAuthorizationBlockConfigSchema.shape.priorAuthorizationInformation.extend({
        copilotDataContext: DocumentTranscriptionResponsesSchema,
        copilotDataContextObject: CopilotDataContextObjectSchema.optional(),
        predictionOptions: z
          .object({
            preamble: z.string().optional(),
            outputRequirement: z.string().optional(),
            outro: z.string().optional(),
          })
          .optional(),
      }),
  });
export type SubmitPriorAuthorizationParsedConfig = z.infer<
  typeof SubmitPriorAuthorizationParsedConfigSchema
>;
